import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Page from "../../components/Page";
import Map from "../../components/Map";
import { fetchAssetMarkersRequest } from '../../core/device/deviceActions';
import { RootState } from '../../core/reducers';
import { AssetMarkerData, DeviceTimelineSegment, MapMode } from '../../types/types';
import TabLink from '../../components/TabLink';
import FilterSidebar from '../../components/FilterSidebar';
import { ErrorBoundary } from 'react-error-boundary';
import AssetSearchSidebar, { SELECT_ALL_VALUE } from '../../components/AssetSearchSidebar';
import dayjs, { Dayjs } from 'dayjs';

const LIVE_DATA_REFRESH_INTERVAL = 30000;

/**
 * Renders the map and controls switching between live and historic modes.
 * We do this here because this is the only use case for the map that isn't static.
 * Other pages will just pipe in a static set of markers with historic mode.
 */
const MapPage = () => {
    const dispatch = useDispatch();
    const [filterSidebarIsOpen, setFilterSidebarIsOpen] = useState<boolean>(true);
    const [searchString, setSearchString] = useState<string>('');
    const [mapMode, setMapMode] = useState<MapMode>(MapMode.Live);
    const [hoveredAsset, setHoveredAsset] = useState<number | null>(null);
    const [timelineData, setTimelineData] = useState<DeviceTimelineSegment[] | null>(null);
    const assetMarkers = useSelector((state: RootState) => state.device.assetMarkers) as AssetMarkerData[];
    const checkedAssets = useSelector((state: RootState) => (state.device.checkedAssets) as string[]);
    const selectedDate = useSelector((state: RootState) => state.device.selectedDate || dayjs() as Dayjs);
    const isFetchingAssetMarkers = useSelector((state: RootState) => (state.device.isFetchingAssetMarkers as boolean) || false);
    const isFetchingSnailTrail = useSelector((state: RootState) => (state.device.isFetchingSnailTrail as boolean) || false);
    const isFetchingGeofences = useSelector((state: RootState) => (state.geofence.isFetchingGeofences as boolean) || false);
    const isFetchingEventTypes = useSelector((state: RootState) => (state.asset?.isFetchingEventTypes as boolean) || false);
    const isFetchingHeatmap = useSelector((state: RootState) => (state.device.isFetchingDeviceHeatMap as boolean) || false);
    const isFetchingCompanyChange = useSelector((state: RootState) => (state.user?.isFetchingCompanyChange as boolean) || false);
    const isLoading = // We do smart spinner to not display when there is already data to view, then just silent merge when ready
        (isFetchingAssetMarkers && !assetMarkers.length) || 
        (isFetchingAssetMarkers && mapMode === MapMode.Historic && checkedAssets.length > 1) ||
        (isFetchingSnailTrail && mapMode === MapMode.Historic) ||
        isFetchingGeofences ||
        isFetchingEventTypes || 
        isFetchingHeatmap || 
        isFetchingCompanyChange;
    const liveDataRefreshIntervalRef = useRef<number | null>(null);
    const SIDEBAR_OPEN_WIDTH = '344px';
    const SIDEBAR_CLOSED_WIDTH = '32px';
    
    /**
     * Clear the live data refresh interval
     */
    const clearLiveDataRefreshInterval = () => {
        if (liveDataRefreshIntervalRef.current !== null) {
            window.clearInterval(liveDataRefreshIntervalRef.current);
            liveDataRefreshIntervalRef.current = null;
        }
    };
    /**
     * Fetch asset markers same as live but filtered by checked assets and including snail trails.
     * If select all is checked then fetches asset ids from all asset markers.
     */
    const fetchHistoricAssetMarkers = () => {
        const checkedAssetIds = checkedAssets.length > 0 && checkedAssets[0] === SELECT_ALL_VALUE
            ? assetMarkers.map((am) => am.asset_id)
            : checkedAssets.map((id) => {
                const type = id.split('-')[0];
                if (type !== 'asset') {
                    return NaN;
                } else {
                    return parseInt(id.split('-')[1]);
                }
            }).filter((id) => !isNaN(id));
        const sanitizedDate = dayjs(selectedDate);
        if (checkedAssetIds?.length) {
            dispatch(fetchAssetMarkersRequest({ 
                withSnailTrails: true,
                assetIds: checkedAssetIds,
                date: sanitizedDate.format('YYYY-MM-DD'),
            }));
        }
    };
    useEffect(() => {
        if (mapMode === MapMode.Live) {
            clearLiveDataRefreshInterval();
            dispatch(fetchAssetMarkersRequest({ withSnailTrails: false }));
            liveDataRefreshIntervalRef.current = window.setInterval(() => {
                dispatch(fetchAssetMarkersRequest({ withSnailTrails: false }));
            }, LIVE_DATA_REFRESH_INTERVAL);
        } else {
            clearLiveDataRefreshInterval();
            if (!isFetchingSnailTrail) {
                fetchHistoricAssetMarkers();
            }
        }
        return () => {
            clearLiveDataRefreshInterval();
        };
    }, [mapMode, dispatch]);
    useEffect(() => {
        if (mapMode === MapMode.Historic) {
            if (!isFetchingSnailTrail) {
                fetchHistoricAssetMarkers();
            }
        }
    }, [checkedAssets])

    return (
        <ErrorBoundary
            fallback={null}
            onError={(error, info) => {
                // Picked up by Sentry, catch and attempt to continue
                console.error('[Map ErrorBoundary]', error, info);
            }}
        >
            <Page 
                title="Fleetclear Map" 
                headerEnabled
                isLoading={isLoading}
            >
                <FilterSidebar 
                    displayFilter
                    onToggleFilterSidebar={(val: boolean) => setFilterSidebarIsOpen(!filterSidebarIsOpen)}
                >
                    <AssetSearchSidebar
                        searchValue={searchString}
                        onChangeSearchValue={e => setSearchString(e.target.value)}
                        assetMarkers={assetMarkers}
                        onHoverAssetLeaf={setHoveredAsset}
                        timelineData={timelineData}
                />
                </FilterSidebar>
                <div style={{ 
                    position: 'absolute', 
                    left: filterSidebarIsOpen ? SIDEBAR_OPEN_WIDTH : SIDEBAR_CLOSED_WIDTH, 
                    top: 0, 
                    transition: 'left 0.3s ease-out', 
                }}>
                    <TabLink 
                        title='Map' 
                        isActive
                    />
                </div>
                <br />
                <Map 
                    toolbarEnabled
                    assetMarkers={assetMarkers} 
                    filterSidebarIsOpen={filterSidebarIsOpen}
                    mapMode={mapMode}
                    onChangeMapMode={(mode: MapMode) => setMapMode(mode)}
                    injectedHoveredAsset={hoveredAsset}
                    parentIsFetchingData={isLoading}
                    onSetTimelineData={(data) => setTimelineData(data)}
            />
            </Page>
        </ErrorBoundary>
    );
}

export default MapPage;